import React, {useCallback, useMemo, useState} from 'react'
import styled from 'styled-components'
import {useTranslation} from 'i18n'
import {requestData} from 'services'
import {showSnackbar} from 'utils'
import {WINDOW_MODE_TABLET_WIDTH} from 'consts'
import convertUnit from 'lib/unit'
import {Icon, Paragraph, ToggleSwitch} from 'common/components'
import {useDispatch, useSelector} from 'lib/redux'

const StyledContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  justify-content: center;
  align-self: center;
  background-color: ${({theme}) => theme.white_3};
`
const StyledContentContainer = styled.div`
  display: flex;
  height: fit-content;
  padding: ${convertUnit(20)};
  flex-direction: column;
  gap: ${convertUnit(20)};
  border-radius: ${convertUnit(16)};
  margin-top: ${convertUnit(20)};
  max-width: ${convertUnit(560)};
  @media (max-width: ${WINDOW_MODE_TABLET_WIDTH}px) {
    max-width: 100%;
    height: 100%;
    margin-top: 0;
    border-radius: 0;
  }
  background-color: ${({theme}) => theme.white_1};
`
const StyledHeaderContainer = styled.div`
  display: flex;
  justify-content: space-between;
`
const StyledDisclaimerContainer = styled.div`
  background-color: ${({theme}) => theme.white_3};
  border-radius: ${convertUnit(8)};
  display: flex;
  flex-direction: row;
  padding: ${convertUnit(8)} ${convertUnit(12)};
  gap: ${convertUnit(12)};
`

export default function GiftShopSettingPrivacy() {
  const {translate} = useTranslation()
  const {is_private} = useSelector('user') || {}
  const {update} = useDispatch()
  const [isPrivateAccount, setIsPrivateAccount] = useState(is_private || false)
  const [loading, setLoading] = useState(false)

  const handleTogglePrivateAccount = useCallback(() => {
    setLoading(true)
    setIsPrivateAccount((prev) => !prev)
    requestData('giftshop_toggle_private_account', {
      data: {is_private: !isPrivateAccount},
      onRequestReceived: () => setLoading(false),
      onRequestSuccess: ({status}) => {
        if (status === 200) {
          update('user', {is_private: !isPrivateAccount})
          showSnackbar(
            translate('giftShop:settingPrivateAccountToggle', {
              context: !isPrivateAccount === true ? 'private' : 'public',
            }),
          )
        }
      },
    })
  }, [isPrivateAccount, translate, update])

  const handleRenderPrivateAccountToggle = useMemo(
    () => (
      <StyledHeaderContainer>
        <Paragraph fontSize="m" fontWeight="medium">
          {translate('giftShop:settingPrivateAccount')}
        </Paragraph>
        <ToggleSwitch
          toggleKey="privateAccount"
          disabled={loading}
          onSwitch={handleTogglePrivateAccount}
          selected={isPrivateAccount}
        />
      </StyledHeaderContainer>
    ),
    [handleTogglePrivateAccount, isPrivateAccount, loading, translate],
  )

  const handleRenderFlavorText = useMemo(
    () => (
      <StyledDisclaimerContainer>
        <Icon type="info" size={convertUnit(16)} />
        <Paragraph fontWeight="medium" lineHeight={21}>
          {translate('giftShop:settingPrivateAccountFlavorText')}
        </Paragraph>
      </StyledDisclaimerContainer>
    ),
    [translate],
  )

  const handleRenderDiscoverability = useMemo(
    () => (
      <>
        <Paragraph fontSize="l" fontWeight="medium" color="info_5">
          {translate('giftShop:settingDiscoverability')}
        </Paragraph>
        {handleRenderPrivateAccountToggle}
        {handleRenderFlavorText}
      </>
    ),
    [handleRenderFlavorText, handleRenderPrivateAccountToggle, translate],
  )

  return (
    <StyledContainer>
      <StyledContentContainer>
        {handleRenderDiscoverability}
      </StyledContentContainer>
    </StyledContainer>
  )
}
